import { CheckCircleIcon, XIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@util/tailwind";

interface SuccessMessageProps {
    message: string;
    description?: string;
    duration?: number;
    className?: string;
    onClose?: () => void;
}

export default function SuccessMessage({
    message,
    description,
    duration = 5000,
    className,
    onClose,
}: SuccessMessageProps) {
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        if (duration > 0) {
            const timer = setTimeout(() => {
                setIsVisible(false);
                if (onClose) onClose();
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [duration, onClose]);

    const handleClose = () => {
        setIsVisible(false);
        if (onClose) onClose();
    };

    if (!isVisible) return null;

    return (
        <div
            className={cn(
                "fixed top-4 right-4 z-50 max-w-md animate-in fade-in slide-in-from-top-2 duration-300",
                className,
            )}
            data-pol-id="927er1"
            data-pol-file-name="success-message"
            data-pol-file-type="component"
        >
            <div
                className="rounded-lg border border-green-100 bg-white p-4 shadow-md"
                data-pol-id="qcm5v5"
                data-pol-file-name="success-message"
                data-pol-file-type="component"
            >
                <div
                    className="flex items-start gap-3"
                    data-pol-id="6h2ngg"
                    data-pol-file-name="success-message"
                    data-pol-file-type="component"
                >
                    <div
                        className="flex-shrink-0"
                        data-pol-id="b2ihaf"
                        data-pol-file-name="success-message"
                        data-pol-file-type="component"
                    >
                        <CheckCircleIcon
                            className="h-5 w-5 text-green-500"
                            data-pol-id="4baypm"
                            data-pol-file-name="success-message"
                            data-pol-file-type="component"
                        />
                    </div>
                    <div
                        className="flex-1"
                        data-pol-id="6k1zy4"
                        data-pol-file-name="success-message"
                        data-pol-file-type="component"
                    >
                        <h3
                            className="font-medium text-gray-900"
                            data-pol-id="gbqyzd"
                            data-pol-file-name="success-message"
                            data-pol-file-type="component"
                        >
                            {message}
                        </h3>
                        {description && (
                            <p
                                className="mt-1 text-sm text-gray-500"
                                data-pol-id="4zdb32"
                                data-pol-file-name="success-message"
                                data-pol-file-type="component"
                            >
                                {description}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        className="ml-4 inline-flex flex-shrink-0 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3118DC] focus:ring-offset-2"
                        onClick={handleClose}
                        data-pol-id="iee8t5"
                        data-pol-file-name="success-message"
                        data-pol-file-type="component"
                    >
                        <span
                            className="sr-only"
                            data-pol-id="fp7scq"
                            data-pol-file-name="success-message"
                            data-pol-file-type="component"
                        >
                            Close
                        </span>
                        <XIcon
                            className="h-5 w-5"
                            data-pol-id="w7vsfu"
                            data-pol-file-name="success-message"
                            data-pol-file-type="component"
                        />
                    </button>
                </div>
            </div>
        </div>
    );
}
